import { CloudActions } from '@portal/portal-core';

import {
  SUPER_ADMIN_SEARCH_LICENSES_SUCCESS,
  SUPER_ADMIN_SEARCH_LICENSES_FAILURE,
  SUPER_ADMIN_CLEAR_LICENSE_SEARCH,
} from '../types';
import type { LicenseSearchResult } from '../actions';

export interface LicenseSearchState extends LicenseSearchResult {
  loading: boolean;
  error: string | null;
  // Id of the most recent search request. Responses for any other id are stale (a newer request
  // was issued, or the search was cleared) and must not overwrite state.
  latestRequestId: string | null;
}

interface LicenseSearchAction {
  type: string;
  result?: LicenseSearchResult;
  error?: string;
  requestId?: string;
}

const initialState: LicenseSearchState = {
  hits: [],
  total: 0,
  limit: 0,
  offset: 0,
  loading: false,
  error: null,
  latestRequestId: null,
};

const LicenseSearchReducer = (
  state: LicenseSearchState = initialState,
  action: LicenseSearchAction,
): LicenseSearchState => {
  switch (action.type) {
    // The debounced search request itself — the trigger dispatched directly from the UI, not a
    // local duck action (mirrors how getInstitutionalLicensesVerbose etc. are dispatched/watched
    // directly by type elsewhere in this duck).
    case CloudActions.ADMIN_ELASTICSEARCH_COST_CENTERS_SEARCH:
      return { ...state, loading: true, error: null, latestRequestId: action.requestId ?? null };
    case SUPER_ADMIN_SEARCH_LICENSES_SUCCESS:
      if (action.requestId !== state.latestRequestId) return state;
      return {
        ...state,
        ...(action.result as LicenseSearchResult),
        loading: false,
        error: null,
      };
    case SUPER_ADMIN_SEARCH_LICENSES_FAILURE:
      if (action.requestId !== state.latestRequestId) return state;
      // Hits from the previous query would otherwise stay on screen under a different search
      // string, looking like results for the failed one.
      return { ...state, hits: [], total: 0, loading: false, error: action.error ?? null };
    case SUPER_ADMIN_CLEAR_LICENSE_SEARCH:
      return initialState;
    default:
      return state;
  }
};

export default LicenseSearchReducer;
